import { Injectable, inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';

/**
 * Wrapper autour de ngx-toastr avec messages explicites en français
 * et options par défaut harmonisées pour tout le projet Dinki.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private toastr = inject(ToastrService);

  private readonly defaultOpts = {
    timeOut: 6000,
    closeButton: true,
    progressBar: true,
    positionClass: 'toast-top-right',
    enableHtml: false,
  };

  success(message: string, title = 'Opération réussie'): void {
    this.toastr.success(message, title, this.defaultOpts);
  }

  info(message: string, title = 'Information'): void {
    this.toastr.info(message, title, this.defaultOpts);
  }

  warning(message: string, title = 'Attention'): void {
    this.toastr.warning(message, title, this.defaultOpts);
  }

  error(message: string, title = 'Erreur'): void {
    this.toastr.error(message, title, {
      ...this.defaultOpts,
      timeOut: 8000,
    });
  }

  /**
   * Convertit une erreur HTTP en message lisible et l'affiche.
   * Gère les ApiResponseDTO du backend Spring Boot ainsi que les
   * erreurs réseau / 401 / 403 / 500.
   */
  fromHttpError(err: any, fallback = 'Une erreur est survenue'): void {
    this.error(this.extractMessage(err, fallback));
  }

  extractMessage(err: any, fallback = 'Une erreur est survenue'): string {
    if (!err) return fallback;
    // Cas spécifique mode offline (levé par offlineInterceptor quand aucune
    // donnée en cache n'est disponible) — message plus actionnable.
    if (err?.error?.offline === true || err?.statusText === 'Offline') {
      return 'Vous êtes hors ligne et ces données ne sont pas encore en cache. Connectez-vous une fois en ligne pour les récupérer.';
    }
    if (err.status === 0) {
      return 'Impossible de joindre le serveur. Vos modifications sont enregistrées localement et seront envoyées au retour du réseau.';
    }
    if (err.status === 401) {
      return 'Session expirée ou identifiants invalides. Veuillez vous reconnecter.';
    }
    if (err.status === 403) {
      return "Accès refusé : vous n'avez pas les droits pour effectuer cette action.";
    }
    if (err.status === 404) {
      return 'Ressource introuvable.';
    }
    if (err.status === 409) {
      return 'Conflit : cette ressource existe déjà ou est en cours de modification.';
    }
    if (err.status === 422 || err.status === 400) {
      const apiMsg = err?.error?.message;
      const validation = this.extractValidationErrors(err);
      return validation || apiMsg || 'Les données envoyées sont invalides.';
    }
    if (err.status === 429) {
      return 'Trop de tentatives. Veuillez patienter avant de réessayer.';
    }
    if (err.status >= 500) {
      return (
        err?.error?.message ||
        'Erreur interne du serveur. Veuillez réessayer plus tard.'
      );
    }
    return err?.error?.message || err?.message || fallback;
  }

  private extractValidationErrors(err: any): string | null {
    const errors = err?.error?.errors;
    if (!errors) return null;
    if (Array.isArray(errors)) {
      return errors
        .map((e: any) => (typeof e === 'string' ? e : e.message || e.field))
        .filter(Boolean)
        .join(' • ');
    }
    if (typeof errors === 'object') {
      return Object.values(errors).join(' • ');
    }
    return null;
  }
}
